import { lazy, Suspense } from "react";
import type { Cell, CellType, Note } from "@fletchy/core";
import { Heart, Monitor, Moon, Plus, SplitSquareHorizontal, Sun, Trash2 } from "lucide-react";
import type { ThemeMode, ViewMode } from "../state/vaultActions";

const CellEditor = lazy(() => import("./CellEditor").then((module) => ({ default: module.CellEditor })));
const RenderedCell = lazy(() => import("./RenderedCell").then((module) => ({ default: module.RenderedCell })));

interface EditorPaneProps {
  selectedNote: Note | null;
  selectedCells: Cell[];
  viewMode: ViewMode;
  theme: ThemeMode;
  isSaving: boolean;
  status: string;
  importSummary: string | null;
  onUpdateNote(patch: Partial<Note>): void;
  onAddCell(type: CellType): void;
  onUpdateCell(cell: Cell): void;
  onDeleteCell(cellId: string): void;
  onMoveCell(cellId: string, direction: "up" | "down"): void;
  onDeleteNote(): void;
  onViewModeChange(viewMode: ViewMode): void;
  onThemeChange(theme: ThemeMode): void;
  onCreateNote(): void;
}

const cellTypes: CellType[] = ["markdown", "code", "latex", "mermaid", "text"];

function parseTags(value: string) {
  return Array.from(
    new Set(
      value
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean),
    ),
  ).sort();
}

export function EditorPane({
  selectedNote,
  selectedCells,
  viewMode,
  theme,
  isSaving,
  status,
  importSummary,
  onUpdateNote,
  onAddCell,
  onUpdateCell,
  onDeleteCell,
  onMoveCell,
  onDeleteNote,
  onViewModeChange,
  onThemeChange,
  onCreateNote,
}: EditorPaneProps) {
  return (
    <section className="editorPane">
      {selectedNote ? (
        <>
          <header className="editorHeader">
            <div className="titleStack">
              <input
                className="titleInput"
                value={selectedNote.title}
                onChange={(event) => onUpdateNote({ title: event.target.value || "Untitled Note" })}
                aria-label="Note title"
              />
              <input
                className="tagInput"
                value={selectedNote.tags.join(", ")}
                onChange={(event) => onUpdateNote({ tags: parseTags(event.target.value) })}
                placeholder="tags"
                aria-label="Tags"
              />
            </div>
            <div className="editorActions">
              <button
                type="button"
                className={selectedNote.favorite ? "iconButton active" : "iconButton"}
                aria-label="Toggle favorite"
                onClick={() => onUpdateNote({ favorite: !selectedNote.favorite })}
              >
                <Heart size={17} fill={selectedNote.favorite ? "currentColor" : "none"} />
              </button>
              <div className="segmented" aria-label="Editor view mode">
                <button type="button" className={viewMode === "edit" ? "active" : ""} onClick={() => onViewModeChange("edit")}>
                  Edit
                </button>
                <button type="button" className={viewMode === "split" ? "active" : ""} onClick={() => onViewModeChange("split")}>
                  <SplitSquareHorizontal size={14} /> Split
                </button>
                <button
                  type="button"
                  className={viewMode === "preview" ? "active" : ""}
                  onClick={() => onViewModeChange("preview")}
                >
                  Preview
                </button>
              </div>
              <div className="segmented compact" aria-label="Theme">
                <button
                  type="button"
                  className={theme === "system" ? "active" : ""}
                  aria-label="System theme"
                  aria-pressed={theme === "system"}
                  title="System theme"
                  onClick={() => onThemeChange("system")}
                >
                  <Monitor size={14} />
                </button>
                <button
                  type="button"
                  className={theme === "light" ? "active" : ""}
                  aria-label="Light theme"
                  aria-pressed={theme === "light"}
                  title="Light theme"
                  onClick={() => onThemeChange("light")}
                >
                  <Sun size={14} />
                </button>
                <button
                  type="button"
                  className={theme === "dark" ? "active" : ""}
                  aria-label="Dark theme"
                  aria-pressed={theme === "dark"}
                  title="Dark theme"
                  onClick={() => onThemeChange("dark")}
                >
                  <Moon size={14} />
                </button>
              </div>
              <button type="button" className="iconButton danger" aria-label="Trash note" onClick={onDeleteNote}>
                <Trash2 size={17} />
              </button>
            </div>
          </header>

          <Suspense fallback={<div className="workspaceLoading">Loading notebook tools...</div>}>
            <div className={`workspace mode-${viewMode}`}>
              {viewMode !== "preview" ? (
                <div className="cellStack">
                  {selectedCells.map((cell, index) => (
                    <CellEditor
                      key={cell.id}
                      cell={cell}
                      canMoveUp={index > 0}
                      canMoveDown={index < selectedCells.length - 1}
                      onChange={onUpdateCell}
                      onDelete={() => onDeleteCell(cell.id)}
                      onMove={(direction) => onMoveCell(cell.id, direction)}
                    />
                  ))}
                  <div className="addCellRow">
                    {cellTypes.map((type) => (
                      <button type="button" key={type} onClick={() => onAddCell(type)}>
                        <Plus size={14} /> {type}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {viewMode !== "edit" ? (
                <div className="previewPane">
                  {selectedCells.map((cell) => (
                    <article className="previewCell" key={cell.id}>
                      <div className="previewType">{cell.type}</div>
                      <RenderedCell cell={cell} />
                    </article>
                  ))}
                </div>
              ) : null}
            </div>
          </Suspense>

          <footer className="statusBar">
            <span>{isSaving ? "Saving..." : status}</span>
            {importSummary ? <span>{importSummary}</span> : null}
          </footer>
        </>
      ) : (
        <div className="emptyEditor">
          <h1>No note selected</h1>
          <button type="button" onClick={onCreateNote}>
            <Plus size={16} /> New note
          </button>
        </div>
      )}
    </section>
  );
}
